import { BillingSubscription, SharedProps, StripeSubscriptionStatus } from '@/types'
import { formatCurrency } from '@/utils/format'
import { Head, router, usePage } from '@inertiajs/react'
import { Badge, Button, Card, Divider, Group, List, Stack, Text, ThemeIcon, Title } from '@mantine/core'
import {
  IconCalendar,
  IconCreditCard,
  IconMessage,
  IconPrinter,
  IconReceipt,
  IconShoppingCart,
  IconUsers,
} from '@tabler/icons-react'
import dayjs from 'dayjs'

const STATUS_LABELS: Record<StripeSubscriptionStatus, string> = {
  active: 'Actif',
  trialing: "Période d'essai",
  past_due: 'Paiement en retard',
  canceled: 'Annulé',
  incomplete: 'Incomplet',
  incomplete_expired: 'Expiré',
  unpaid: 'Impayé',
  paused: 'Suspendu',
}

const STATUS_COLORS: Record<StripeSubscriptionStatus, string> = {
  active: 'green',
  trialing: 'blue',
  past_due: 'orange',
  canceled: 'gray',
  incomplete: 'orange',
  incomplete_expired: 'red',
  unpaid: 'red',
  paused: 'gray',
}

const CARD_BRAND_LABELS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
}

export default function BillingShowPage({
  subscription,
  subscription_price,
}: {
  subscription: BillingSubscription | null
  subscription_price: number | null
}) {
  const { current_organization } = usePage<SharedProps>().props
  if (!current_organization) return null

  const handleSubscribe = () => router.post(`/organizations/${current_organization.slug}/billing`)
  const handleManage = () => router.post(`/organizations/${current_organization.slug}/billing_portal`)

  const displayAmount = subscription?.amount_ht ?? subscription_price

  return (
    <>
      <Head title={`Abonnement - ${current_organization.name}`} />

      <Stack maw={500} mx="auto" w="100%">
        <Title ta="center" order={2}>
          Abonnement
        </Title>

        {subscription ? (
          <Card withBorder radius="lg" padding="xl" shadow="sm">
            <Stack gap="lg">
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Text size="sm" c="dimmed">
                    Abonnement Seamflow
                  </Text>
                  <Group gap={4} align="baseline">
                    <Text size="xl" fw={700}>
                      {displayAmount != null ? formatCurrency(displayAmount) : '—'}
                    </Text>
                    <Text size="sm" c="dimmed">
                      HT / mois
                    </Text>
                  </Group>
                </Stack>
                <Badge
                  color={subscription.canceling ? 'orange' : STATUS_COLORS[subscription.status]}
                  size="lg"
                  variant="light"
                >
                  {subscription.canceling ? 'Annulation prévue' : STATUS_LABELS[subscription.status]}
                </Badge>
              </Group>

              <Divider />

              <Stack gap="sm">
                {(subscription.cancel_date || subscription.current_period_end) && (
                  <Group gap="sm">
                    <ThemeIcon variant="light" size="sm" color={subscription.canceling ? 'orange' : 'gray'}>
                      <IconCalendar size={14} />
                    </ThemeIcon>
                    <Text size="sm" c={subscription.canceling ? 'orange' : 'dimmed'}>
                      {subscription.canceling ? "Fin de l'abonnement" : 'Prochain renouvellement'}
                    </Text>
                    <Text size="sm" fw={500} ml="auto" c={subscription.canceling ? 'orange' : undefined}>
                      {dayjs(subscription.cancel_date ?? subscription.current_period_end).format('DD MMMM YYYY')}
                    </Text>
                  </Group>
                )}

                {subscription.card && (
                  <Group gap="sm">
                    <ThemeIcon variant="light" size="sm" color="gray">
                      <IconCreditCard size={14} />
                    </ThemeIcon>
                    <Text size="sm" c="dimmed">
                      Moyen de paiement
                    </Text>
                    <Text size="sm" fw={500} ml="auto">
                      {CARD_BRAND_LABELS[subscription.card.brand] ?? subscription.card.brand} •••• {subscription.card.last_four}
                    </Text>
                  </Group>
                )}

                {subscription.created && (
                  <Group gap="sm">
                    <ThemeIcon variant="light" size="sm" color="gray">
                      <IconReceipt size={14} />
                    </ThemeIcon>
                    <Text size="sm" c="dimmed">
                      Abonné depuis
                    </Text>
                    <Text size="sm" fw={500} ml="auto">
                      {dayjs(subscription.created).format('DD MMMM YYYY')}
                    </Text>
                  </Group>
                )}
              </Stack>

              <Divider />

              <Button
                onClick={handleManage}
                leftSection={<IconCreditCard size={18} />}
                size="md"
                radius="md"
                variant="light"
                fullWidth
              >
                Gérer mon abonnement
              </Button>

              {subscription.canceling && (
                <Button onClick={handleManage} size="md" radius="md" fullWidth>
                  Réactiver l'abonnement
                </Button>
              )}
            </Stack>
          </Card>
        ) : subscription_price == null ? (
          <Card withBorder radius="lg" padding="xl" shadow="sm">
            <Stack gap="sm" align="center">
              <Text size="lg" fw={600}>
                Aucun abonnement requis
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                Votre compte est actif sans frais d'abonnement.
              </Text>
            </Stack>
          </Card>
        ) : (
          <Card withBorder radius="lg" padding="xl" shadow="sm">
            <Stack gap="lg">
              <Stack gap={4} align="center">
                <Text size="lg" fw={600}>
                  Abonnement Seamflow
                </Text>
                <Group gap={4} align="baseline">
                  <Text fz={36} fw={700}>
                    {formatCurrency(subscription_price)}
                  </Text>
                  <Text c="dimmed">HT / mois</Text>
                </Group>
                <Text size="sm" c="dimmed">
                  {formatCurrency(subscription_price * 1.2)} TTC avec TVA (20%)
                </Text>
              </Stack>

              <Divider />

              <List spacing="sm" center>
                <List.Item icon={<ThemeIcon color="blue" size="sm" variant="light"><IconShoppingCart size={14} /></ThemeIcon>}>
                  <Text size="sm">Gestion des commandes et du comptoir</Text>
                </List.Item>
                <List.Item icon={<ThemeIcon color="blue" size="sm" variant="light"><IconUsers size={14} /></ThemeIcon>}>
                  <Text size="sm">Suivi clients particuliers et professionnels</Text>
                </List.Item>
                <List.Item icon={<ThemeIcon color="blue" size="sm" variant="light"><IconPrinter size={14} /></ThemeIcon>}>
                  <Text size="sm">Impression de tickets et bons de dépose</Text>
                </List.Item>
                <List.Item icon={<ThemeIcon color="blue" size="sm" variant="light"><IconMessage size={14} /></ThemeIcon>}>
                  <Text size="sm">Envoi de SMS aux clients</Text>
                </List.Item>
                <List.Item icon={<ThemeIcon color="blue" size="sm" variant="light"><IconReceipt size={14} /></ThemeIcon>}>
                  <Text size="sm">Z de caisse et récapitulatifs</Text>
                </List.Item>
              </List>

              <Button onClick={handleSubscribe} leftSection={<IconCreditCard size={18} />} size="lg" radius="md" fullWidth>
                Souscrire
              </Button>
            </Stack>
          </Card>
        )}
      </Stack>
    </>
  )
}
